import QRCode from 'qrcode';

import { toast } from 'react-hot-toast';
import { action, observable, runInAction } from 'mobx';

import { ButtonClickEvent } from 'ui/base/types';
import { BooleanStore, StringStore } from 'ui/base/input';
import { BusyStore } from 'ui/base/button';

import { Optional, ResultKind } from 'services/base/types';
import { setClipboard } from 'services/base/ipc';
import { IPangolinDomain, VPNClient } from 'services/pangolin/types';

import {
  VPNClientDeleteResult,
  VPNClientService,
  VPNClientUpdateResult,
} from 'services/pangolin/vpn_clients_service';

import Messages from './edit_vpn_client_messages';

type PartialEditVPNClientBase = Pick<VPNClient, 'name'> & Partial<VPNClient>;
type PartialEditVPNClientWithID = Pick<VPNClient, 'id'> & PartialEditVPNClientBase;
type PartialEditVPNClient = PartialEditVPNClientBase | PartialEditVPNClientWithID;

export class EditVPNClientStore {
  private readonly client: Optional<VPNClient>;
  name: StringStore;
  qrCodeShow: BooleanStore;
  qrCodeData: StringStore;

  @observable.ref
  nameError?: string;

  @observable.ref
  busy: BusyStore;

  constructor(client: Optional<VPNClient>) {
    this.client = client;
    this.name = new StringStore(client?.name ?? '');
    this.qrCodeShow = new BooleanStore(true);
    this.qrCodeData = new StringStore('');
    this.busy = new BusyStore();
    this.qrCodeCompile();
  }

  async qrCodeCompile() {
    const nonce = this.client?.nonce;
    if (nonce != null) {
      const data = await QRCode.toDataURL(nonce);
      this.qrCodeData.setValue(data);
    }
  }

  @action.bound
  validate(client: PartialEditVPNClient): Optional<VPNClientUpdateResult> {
    let valid = true;
    if (client.name.trim().length === 0) {
      this.nameError = Messages.invalidName();
      valid = false;
    }

    return valid ? undefined : {
      kind: ResultKind.USER_ERROR,
      nameError: this.nameError,
    };
  }

  convertToVPNClient(): PartialEditVPNClient {
    return this.client == null ? {
      name: this.name.value,
    } : {
      name: this.name.value,
      id: this.client.id,
    };
  }

  @action.bound
  toggleNonce(event: ButtonClickEvent) {
    event.preventDefault();
    this.qrCodeShow.setValue(!this.qrCodeShow.value);
  }

  @action.bound
  async handleCopyQRCode() {
    if (this.client?.mnemonic != null) {
      try {
        await setClipboard(this.client.mnemonic);
        toast.success(Messages.copySuccess());
      } catch {
        toast.error(Messages.copyError());
      }
    }
  }

  @action.bound
  async handleSave(domain: IPangolinDomain): Promise<VPNClientUpdateResult> {
    this.nameError = undefined;

    const client = this.convertToVPNClient();

    // Validate client-side
    const localResult = this.validate(client);
    if (localResult?.kind === ResultKind.USER_ERROR) {
      return localResult;
    }

    if (client.id == null) {
      const data = client as PartialEditVPNClientBase;
      const result = await VPNClientService.create(domain, data);
      return runInAction(() => {
        if (result.kind === ResultKind.USER_ERROR) {
          this.nameError = result.nameError;
        }
        return result;
      });
    } else {
      const data = client as PartialEditVPNClientWithID;
      const result = await VPNClientService.update(domain, data);
      return runInAction(() => {
        if (result.kind === ResultKind.USER_ERROR) {
          this.nameError = result.nameError;
        }
        return result;
      });
    }
  }

  @action.bound
  handleDelete(domain: IPangolinDomain): Promise<VPNClientDeleteResult> {
    if (this.client == null) {
      return Promise.resolve({ kind: ResultKind.OK });
    } else {
      return VPNClientService.delete(domain, this.client);
    }
  }
}
